<script lang="ts">
	import type { PageData } from './$types';
	import YouTubeLite from '$components/YouTubeLite.svelte';
	import type { returnedWorkType, returnedEventType } from './+page.server';

	interface Props {
		data: PageData;
	}

	let { data }: Props = $props();

	interface FrontPageUpdate {
		title?: string;
		linkUrl?: string;
		dateRange?: string;
		subtitle?: string;
	}

	const updates: FrontPageUpdate[] = [
		{
			title: 'Streetwise Opera wins the 2025 RPS Award for Re:Discover',
			linkUrl: 'https://streetwiseopera.org/2025-rps-awards-announcement/',
			dateRange: '2025',
			subtitle: 'Sound design for three new operas, in Manchester, Sheffield, and London.'
		},
		{
			title: 'Britten Sinfonia Opus 1',
			linkUrl:
				'https://web.archive.org/web/20241015194904/https://www.brittensinfonia.com/stories/sam-greening',
			dateRange: '2024',
			subtitle: undefined
		}
	];

	let { recentWorks, upcomingEvents } = $derived(
		data as {
			recentWorks: returnedWorkType[];
			upcomingEvents: returnedEventType[];
		}
	);
</script>

<svelte:head>
	<title>Sam Greening</title>
	<meta name="description" content="Sam Greening. Composer, pianist, electronic musician." />
	<link rel="preconnect" href="https://w.soundcloud.com/" />
</svelte:head>

<main class="flex flex-col justify-evenly">
	<section class="text-6xl">
		<h1 class="my-8">Sam Greening</h1>
		<h2 class="my-8">
			<!-- eslint-disable svelte/no-navigation-without-resolve -->
			<a class="link-bg-hl text-red-500" href="/works" data-sveltekit-preload-data>Composer</a>
			and
			<span class="highlight">Pianist</span>
		</h2>
	</section>

	<section class="mt6">
		<a href="/works" class="text-link">
			<h3 class="my-4 border-b-2 border-current text-lg">Recent Works</h3>
		</a>
		<ul class="pa0 ma0">
			<!-- eslint-disable-next-line svelte/require-each-key -->
			{#each recentWorks as work}
				<li class="mv2 db leading-tight">
					<a
						class="dark-gray hover-bg-washed-blue hide-child db w-100 min-h2 pv1 ph2 ma0 inline-flex items-center hover:text-blue-500"
						href="works/{work.slug}"
						data-sveltekit-preload-data
					>
						<div class="flex flex-col leading-tight">
							<div class="flex flex-row items-baseline">
								<div class="underline-child f5">{work.name}</div>
								{#if work.dateRange}
									<div class="ph2">({work.dateRange})</div>
								{/if}
							</div>
							{#if work.instrumentation}
								<div class="i">
									for {work.instrumentation}
								</div>
							{:else if work.summaryDetails}
								<div class="i">
									{work.summaryDetails}
								</div>
							{/if}
						</div>
						<div class="flex-grow"></div>
						<div class="child tr f3">&gt;</div>
					</a>
				</li>
			{/each}
		</ul>
	</section>

	<!-- eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -->
	{#if upcomingEvents?.length}
		<section class="mt6">
			<a href="/events" class="text-link">
				<h3 class="my-4 border-b-2 border-current text-lg">Upcoming Events</h3>
			</a>
			<ul class="pa0 ma0">
				<!-- eslint-disable-next-line svelte/require-each-key -->
				{#each upcomingEvents as event}
					<li class="mv2 db leading-tight">
						<a
							class="dark-gray hover-bg-washed-blue hide-child db w-100 min-h2 pv1 ph2 ma0 inline-flex items-center hover:text-blue-500"
							href="events/{event.slug}"
							data-sveltekit-preload-data
						>
							<div class="flex flex-col leading-tight">
								<div class="flex flex-row items-baseline">
									<div class="underline-child f5">{event.name}</div>
								</div>
								<div class="i">
									{event.startTime}
									{#if event.location}
										-
										{event.location.join(', ')}
									{/if}
								</div>
							</div>
							<div class="flex-grow"></div>
							<div class="child tr f3">&gt;</div>
						</a>
					</li>
				{/each}
			</ul>
		</section>
	{/if}

	<section class="mt6">
		<div>
			<h3 class="my-4 border-b-2 border-current text-lg">Updates</h3>
		</div>
		<ul class="pa0 ma0">
			<li class="mv2 db leading-tight">
				<a
					class="dark-gray hover-bg-washed-blue hide-child db w-100 min-h2 pv1 ph2 ma0 inline-flex items-center hover:text-blue-500"
					href="https://www.grnng.com/speck-seam"
					target="_blank"
					rel="noreferrer"
					data-sveltekit-preload-data
				>
					<div class="flex flex-col leading-tight">
						<div class="flex flex-row items-baseline">
							<div class="underline-child f5">
								New electronic music as <i>greening.</i> Purchase on Bandcamp.
							</div>
						</div>
					</div>
					<div class="flex-grow"></div>
					<div class="child tr f3">&gt;</div>
				</a>
				<div
					class="dark-gray hover-bg-washed-blue hide-child db w-100 min-h2 pv1 ph2 ma0 inline-flex items-center hover:text-blue-500"
					data-sveltekit-preload-data
				>
					<iframe
						title="Bandcamp Album Embed"
						style="border: 0; width: 100%; height: 120px;"
						src="https://bandcamp.com/EmbeddedPlayer/album=243002357/size=large/bgcol=ffffff/linkcol=0687f5/artwork=small/transparent=true/tracklist=false/tracks=389250031/esig=4045b2a5b5e9df67afec5af942d155fe/"
						seamless
					>
						<a href="https://grnng.bandcamp.com/album/speck-seam">speck | seam by greening.</a>
					</iframe>
				</div>
			</li>
			<!-- eslint-disable-next-line svelte/require-each-key -->
			{#each updates as update}
				<li class="mv2 db leading-tight">
					<a
						class="dark-gray hover-bg-washed-blue hide-child db w-100 min-h2 pv1 ph2 ma0 inline-flex items-center hover:text-blue-500"
						href={update.linkUrl}
						target="_blank"
						rel="noreferrer"
						data-sveltekit-preload-data
					>
						<div class="flex flex-col leading-tight">
							<div class="flex flex-row items-baseline">
								<div class="underline-child f5">{update.title}</div>
								{#if update.dateRange}
									<div class="ph2">({update.dateRange})</div>
								{/if}
							</div>
							{#if update.subtitle}
								<div class="i">
									{update.subtitle}
								</div>
							{/if}
						</div>
						<div class="flex-grow"></div>
						<div class="child tr f3">&gt;</div>
					</a>
				</li>
			{/each}
		</ul>
	</section>

	<section>
		<a href="https://soundcloud.com/samgreening" class="text-link">
			<h3 class="my-4 border-b-2 border-current text-lg">Recent Recordings</h3>
		</a>
		<div class="embed soundcloud-player">
			<iframe
				loading="lazy"
				title="SoundCloud Player"
				width="100%"
				height="300"
				scrolling="no"
				frameborder="no"
				allow="autoplay"
				src="https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/1179245461&color=%234f6cec&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true"
			></iframe>
		</div>
		<div class="mt-2">
			<YouTubeLite id="vSBfrMHkFMI" title="Recording of SHATTER" />
		</div>
	</section>
</main>
